import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { Header, Label, Popup, Icon, Form } from 'semantic-ui-react';
import ActionsTableContainer from '../../../containers/chart/ActionsTableContainer';
import TrendLabelsContainer from '../../../containers/chart/TrendLabelsContainer';
import { DATE_FORMAT, ENTITY_TYPE, INVERTED_THEME, ENTITY_PROPERTY, EMPTY_STRING } from '../../../utils/constants';
import EntityText from '../../entity/EntityText';
import styles from './TrendTimeline.styles';
import _ from 'lodash';
import TypesElement from 'components/entity/TypesElement';
import entityConfig from 'utils/entities';
import { injectIntl } from 'react-intl';

const TrendLabel = injectIntl(({
  trend,
  start,
  end,
  overlap = 1,
  overlapIndex = 0,
  highlighted,
  allTrendTypes,
  height,
  onClickTrend,
  intl: {
    formatMessage
  }
}) => {
  const scaleFactor = Math.max(2, overlap);
  return (
    <Popup
      inverted={INVERTED_THEME}
      style={styles.trendLabelPopup}
      wide
      onMouseMove={e => e.stopPropagation()}
      onClick={e => e.stopPropagation()}
      hoverable
      basic
      trigger={(
        <Label
          color={highlighted
            ? 'purple'
            : _.get(allTrendTypes[trend.type], ENTITY_PROPERTY.TREND_TYPE.COLOUR)}
          circular={start === end}
          onClick={e => {
            onClickTrend && onClickTrend(trend);
            e.stopPropagation();
          }}
          onMouseMove={e => e.stopPropagation()}
          style={{ 
            left: `${Math.max(0, start)}%`,
            width: start === end
              ? null
              : `${Math.min(100, end) - Math.max(0, start)}%`,
            top: overlap > 1 ? `${100 * overlapIndex / overlap}%` : undefined,
            height: `${Math.min(25, height / scaleFactor)}px`,
            padding: `${(height / 5) / scaleFactor}px`,
            lineHeight: `${(height / 2) / scaleFactor}px`,
            fontSize: `${(height / 50) * 2 / scaleFactor}em`,
            opacity: 1,
            transform: start === end
              ? 'translate(-50%)'
              : null,
            borderTopLeftRadius: start < 0 ? 0 : undefined,
            borderBottomLeftRadius: start < 0 ? 0 : undefined,
            borderTopRightRadius: end > 100 ? 0 : undefined,
            borderBottomRightRadius: end > 100 ? 0 : undefined,
            ...styles.trendLabel
          }}>
          <Icon
            style={{
              fontSize: `${(3 * height / 5) / scaleFactor}px`
            }}
            fitted={start === end}
            name={_.get(allTrendTypes[trend.type], ENTITY_PROPERTY.TREND_TYPE.ICON)} />
          {start === end
            ? EMPTY_STRING
            : ` ${trend.name}`}
        </Label>
      )}
      on='hover'
      position='bottom center'>
      <Header
        inverted={INVERTED_THEME}
        textAlign='center'>
        {trend.name}        
        <Header.Subheader style={styles.trendLabelUnitSubheader}>
          <EntityText
            id={trend.unitId}
            entityType={ENTITY_TYPE.UNIT} />
        </Header.Subheader>
        <Header.Subheader>
          {moment(trend.to).local().format(DATE_FORMAT)}
        </Header.Subheader>
      </Header>
      <div style={styles.trendLabelPopupContent}>
        <Form
          inverted={INVERTED_THEME}>
          <Form.TextArea
            label={formatMessage(entityConfig.trend.messages['form.description.label'])}
            value={trend.description}
            readOnly />
          <TrendLabelsContainer
            trend={trend}
            label={formatMessage(entityConfig.trend.messages['form.labels.label'])} />
          {trend && (
            <TypesElement
              typesById={allTrendTypes}
              selectedTypeId={trend.type}
              settingsValues={trend.settings}
              entityMessages={entityConfig.trend.messages}
              readonly
              lockType />
          )}
        </Form>
        <Header
          style={styles.trendLabelDividingHeader}
          dividing
          content='Actions' />
        <ActionsTableContainer trend={trend} />
      </div>
    </Popup>
  );
});

TrendLabel.propTypes = {
  trend: PropTypes.object.isRequired,
  start: PropTypes.number.isRequired,
  end: PropTypes.number.isRequired,
  overlap: PropTypes.number,
  overlapIndex: PropTypes.number,
  highlighted: PropTypes.bool.isRequired,
  onClickTrend: PropTypes.func
};

TrendLabel.displayName = 'TrendLabel';

export default TrendLabel;